/**
 * The triage workspace — one scrolling page per report request.
 *
 * Stages render as sections (Define · Subjects · Identification ·
 * Partners · Launch; legacy flows drop Identification, and `plan_built`
 * is invisible — folded into Partners). A sticky gate strip under the
 * identity header is the ONLY advance affordance: progress segments,
 * blocker chips deep-linked to their owning section (from the read-only
 * /gates endpoint), one primary, and an overflow with Revert…/Reject….
 * Completed sections collapse to data summaries whose "Change" opens
 * the revert modal pre-targeted (cascades are stated, never silent).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Code,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';

import { CaseSharing } from '@/components/CaseSharing';
import { EntityChatAside } from '@/components/Chat';
import { EditableCodename } from '@/components/EditableCodename';
import { NpAssignmentAside, NpAssignmentsProvider } from '@/components/NpAssignments';
import { TriageSubjectsPanel } from '@/components/TriageSubjectsPanel';
import {
  DefineSection,
  FamilyStrip,
  GateStrip,
  GateStripPrimary,
  IdentificationSection,
  LaunchSection,
  PartnersSection,
  StageSection,
  StageSectionState,
  SummaryRow,
  WorkspaceRail,
} from '@/components/TriageWorkspace';
import { useLayoutOptional } from '@/contexts/LayoutContext';
import { useReportConfigs, useTeams } from '@/hooks';
import { useCaseRedirect } from '@/hooks/useCaseRedirect';
import { useReportRequestTriage } from '@/hooks/useReportRequests';
import { useTriageGates } from '@/hooks/useTriageGates';
import { ApiConflictError } from '@/services/vnext-api';
import type { ReportConfigSummary } from '@/types/report-config';
import {
  ReportRequestDetail,
  sectionForStage,
  TriageGateBlocker,
  TriageSectionSlug,
  TriageStage,
  triageStageLabel,
  WorkspaceSection,
  workspaceSectionsFor,
} from '@/types/report-request';
import { formatMoney } from '@/utils';

/**
 * The request workspace as a mountable component — the case page embeds
 * it per selected chain node; the standalone route wraps it after its
 * case redirect fails (orphan fallback).
 */
export function TriageRequestWorkspace({
  requestId,
  embedded = false,
  onFamilyChanged,
  onFamilyRenamed,
}: Readonly<{
  requestId: string | undefined;
  embedded?: boolean;
  /** Fired when the codename family gains members (e.g. a split) so the
   * case chain can refresh immediately instead of waiting for its poll. */
  onFamilyChanged?: () => void;
  /** Fired with the new family key after a rename. The family key is the
   * case's URL identity, so the case page must follow it. */
  onFamilyRenamed?: (newFamilyKey: string) => void;
}>) {
  const triage = useReportRequestTriage(requestId);
  const { detail, isLoading, error, refetch } = triage;
  const registerAside = useLayoutOptional()?.registerAside;

  useEffect(() => {
    if (!detail || !registerAside) {
      return;
    }
    // The Partners section registers its own assignment-aware aside while
    // np_selected is active (sharing the section's provider store) — skip
    // the default chat aside there.
    if (detail.triageStage === 'np_selected') {
      return;
    }
    const title = `Triage · ${detail.codename ?? `${detail.id.slice(0, 8)}…`}`;
    // Downstream of partner selection the conversations must stay
    // reachable — the Partners section is a collapsed record then, so the
    // NP chat aside registers here with its own provider. A late draft
    // locked to Armadillo from it changes the registry-documents gate the
    // detail carries, so the detail is re-read on a lock change.
    if (detail.triageStage === 'ready_to_start' || detail.triageStage === 'started') {
      registerAside({
        title,
        content: (
          <NpAssignmentsProvider
            reportId={detail.reportId}
            reportRequestId={detail.id}
            onLockChanged={refetch}
          >
            <NpAssignmentAside />
          </NpAssignmentsProvider>
        ),
      });
      return () => {
        registerAside(null);
      };
    }
    registerAside({
      title,
      content: <EntityChatAside entityType="report_request" entityId={detail.id} />,
    });
    return () => {
      registerAside(null);
    };
  }, [detail, refetch, registerAside]);

  return (
    <>
      {isLoading && !detail && (
        <Center h={300}>
          <Loader />
        </Center>
      )}
      {error && (
        <Center h={300}>
          <Text c="red">{error.message}</Text>
        </Center>
      )}
      {detail && (
        <TriageWorkspace
          detail={detail}
          triage={triage}
          embedded={embedded}
          onFamilyChanged={onFamilyChanged}
          onFamilyRenamed={onFamilyRenamed}
        />
      )}
    </>
  );
}

export function PendingTriageDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  // Canonical surface is the case page — resolve the family and redirect,
  // keeping this standalone render as the orphan fallback.
  const redirect = useCaseRedirect('report_request', 'rr', requestId);

  if (redirect === 'resolving') {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="flex-start">
          <Button variant="subtle" size="xs" onClick={() => navigate('/inbox')}>
            ← Back to inbox
          </Button>
        </Group>
        {/* key= remounts on param-only navigation (React Router reuses the
            page instance), so section state never leaks between RRs. */}
        <TriageRequestWorkspace key={requestId} requestId={requestId} />
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Workspace body
// ---------------------------------------------------------------------------

interface TriageWorkspaceProps {
  detail: ReportRequestDetail;
  triage: ReturnType<typeof useReportRequestTriage>;
  /** Case-page mount: the chain rail owns the family, so the inline
   * FamilyStrip is redundant there. */
  embedded?: boolean;
  onFamilyChanged?: () => void;
  onFamilyRenamed?: (newFamilyKey: string) => void;
}

function TriageWorkspace({
  detail,
  triage,
  embedded = false,
  onFamilyChanged,
  onFamilyRenamed,
}: Readonly<TriageWorkspaceProps>) {
  const { teams } = useTeams();
  const teamName = useMemo(() => {
    const team = teams.find((t) => String(t.id) === String(detail.teamId ?? ''));
    return team?.name ?? null;
  }, [teams, detail.teamId]);

  // Split fans this RR's subjects out into sibling requests — new chips
  // appear in the case chain right away via onFamilyChanged.
  const handleSplit = useCallback(async () => {
    await triage.splitRequest();
    onFamilyChanged?.();
  }, [triage, onFamilyChanged]);

  const sections = useMemo(
    () => workspaceSectionsFor(detail.effectiveStages),
    [detail.effectiveStages]
  );
  const isRecord = detail.triageStage === 'started' || detail.triageStage === 'rejected';
  const currentSlug = sectionForStage(detail.triageStage);
  const currentIdx = sections.findIndex((s) => s.slug === currentSlug);

  // The detail object is the refresh key: every mutation that reloads it
  // (candidate selection, subject edits, …) re-evaluates the blockers.
  const gates = useTriageGates(detail.id, detail.triageStage, detail);

  // --- expansion: single expanded section, addressed by ?section= -------
  const [searchParams, setSearchParams] = useSearchParams();
  const paramSlug = searchParams.get('section') as TriageSectionSlug | null;
  const expandedSlug: TriageSectionSlug | null = isRecord ? paramSlug : (paramSlug ?? currentSlug);
  const [flashKeys, setFlashKeys] = useState<Record<string, number>>({});

  const setExpanded = useCallback(
    (slug: TriageSectionSlug | null, push = true) => {
      const next = new URLSearchParams(searchParams);
      if (slug) {
        next.set('section', slug);
      } else {
        next.delete('section');
      }
      setSearchParams(next, { replace: !push });
    },
    [searchParams, setSearchParams]
  );

  // On a stage change, re-point the expansion at the new active section
  // so the workspace always opens where the work is.
  const prevStage = useRef(detail.triageStage);
  useEffect(() => {
    if (prevStage.current !== detail.triageStage) {
      prevStage.current = detail.triageStage;
      if (currentSlug) {
        setExpanded(currentSlug);
      }
    }
  }, [detail.triageStage, currentSlug, setExpanded]);

  // --- blocker chip deep-link: expand + scroll + flash -------------------
  const jumpToSection = useCallback(
    (slug: TriageSectionSlug | string) => {
      const known = sections.some((s) => s.slug === slug);
      if (!known) {
        return;
      }
      setExpanded(slug as TriageSectionSlug);
      setFlashKeys((prev) => ({ ...prev, [slug]: (prev[slug] ?? 0) + 1 }));
      // Scroll after the expand has painted.
      requestAnimationFrame(() => {
        const el = document.getElementById(`section-${slug}`);
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
        el?.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      });
    },
    [sections, setExpanded]
  );

  const onBlockerClick = useCallback(
    (blocker: TriageGateBlocker) => jumpToSection(blocker.target),
    [jumpToSection]
  );

  // --- revert / reject ----------------------------------------------------
  const [revertTarget, setRevertTarget] = useState<TriageStage | null>(null);
  const [revertOpen, { open: openRevert, close: closeRevert }] = useDisclosure(false);
  const [rejectOpen, { open: openReject, close: closeReject }] = useDisclosure(false);

  const canRevert = !isRecord && currentIdx > 0;
  const canReject = !isRecord;

  const openRevertAt = useCallback(
    (target: TriageStage | null) => {
      setRevertTarget(target);
      openRevert();
    },
    [openRevert]
  );

  // --- primary action per stage -------------------------------------------
  const defineSubmitRef = useRef<(() => Promise<void>) | null>(null);
  const registerDefineSubmit = useCallback((submit: (() => Promise<void>) | null) => {
    defineSubmitRef.current = submit;
  }, []);

  const [primaryBusy, setPrimaryBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [partnersConflict, setPartnersConflict] = useState<Record<string, unknown> | null>(null);
  // A config picked in Define seeds the rail's commercials form with the
  // config's default TAT/price (the rail owns commercials now).
  const [configSeed, setConfigSeed] = useState<ReportConfigSummary | null>(null);

  const runPrimary = useCallback(
    async (label: string | null, action: () => Promise<void>) => {
      setPrimaryBusy(true);
      setBusyLabel(label);
      try {
        await action();
      } catch {
        // Hook-level notifications already surfaced the failure; 409
        // details for Partners are captured below before rethrow-catch.
      } finally {
        setPrimaryBusy(false);
        setBusyLabel(null);
        void gates.refetch();
      }
    },
    [gates]
  );

  const legacy = detail.legacyFlow;
  const hasBlocking = gates.blocking.length > 0;

  const primary: GateStripPrimary | null = useMemo(() => {
    switch (detail.triageStage) {
      case 'initial':
        return {
          label: 'Save & continue to Subjects',
          loading: primaryBusy,
          onClick: () =>
            runPrimary('Extracting subjects…', async () => {
              await defineSubmitRef.current?.();
              await triage.advance('subjects_extracted');
            }),
        };
      case 'subjects_extracted':
        return {
          label: legacy ? 'Continue to Partners' : 'Continue to Identification',
          loading: primaryBusy,
          disabled: hasBlocking,
          onClick: () =>
            runPrimary(legacy ? 'Preparing partners…' : 'Firing identification…', async () => {
              await triage.advance(legacy ? 'np_selected' : 'subjects_identified');
            }),
        };
      case 'subjects_identified':
        // Identification → Partners is a chained double advance —
        // plan_built is invisible in the workspace. If the second hop
        // fails the request parks at plan_built and the primary below
        // retries just the remaining hop.
        return {
          label: 'Continue to Partners',
          loading: primaryBusy,
          disabled: hasBlocking,
          onClick: () =>
            runPrimary('Building the research plan…', async () => {
              await triage.advance('plan_built');
              await triage.advance('np_selected');
            }),
        };
      case 'plan_built':
        // Straggler (legacy mid-flight, or a failed second hop above).
        return {
          label: 'Continue to Partners',
          loading: primaryBusy,
          onClick: () =>
            runPrimary(null, async () => {
              await triage.advance('np_selected');
            }),
        };
      case 'np_selected':
        return {
          label: 'Continue to Start',
          loading: primaryBusy,
          disabled: hasBlocking,
          onClick: () =>
            runPrimary('Creating the Backoffice project…', async () => {
              setPartnersConflict(null);
              try {
                await triage.advance('ready_to_start');
              } catch (err) {
                if (err instanceof ApiConflictError) {
                  setPartnersConflict(err.detail);
                  jumpToSection('partners');
                }
              }
            }),
        };
      case 'ready_to_start':
        return {
          label: 'Start report',
          loading: primaryBusy,
          onClick: () =>
            runPrimary('Starting…', async () => {
              await triage.start();
            }),
        };
      default:
        return null;
    }
  }, [detail.triageStage, legacy, primaryBusy, hasBlocking, runPrimary, triage, jumpToSection]);

  // Subject-targeted gate blockers, keyed by subject id → what's missing.
  // The Subjects panel highlights the matching cards so the analyst can
  // see WHICH subject blocks Continue, not just that something does.
  const subjectAttention = useMemo(() => {
    const labels: Record<string, string> = {
      missing_jurisdiction: 'jurisdiction',
      missing_subject_type: 'subject type',
    };
    const map: Record<string, string[]> = {};
    for (const blocker of gates.gates?.blockers ?? []) {
      const subjectId = blocker.refs?.subject_id;
      const label = labels[blocker.code];
      if (blocker.target === 'subjects' && typeof subjectId === 'string' && label) {
        (map[subjectId] ??= []).push(label);
      }
    }
    return map;
  }, [gates.gates]);

  // --- summaries ------------------------------------------------------------
  const subjectCount = detail.subjectStrings?.length ?? 0;

  const summaryFor = (slug: TriageSectionSlug): React.ReactNode => {
    switch (slug) {
      case 'define':
        return (
          <SummaryRow
            parts={[
              teamName ?? detail.teamId ?? 'No team',
              detail.configName ?? 'No config',
              formatMoney(detail.price, detail.currency),
              detail.turnaroundTimeDays != null ? `${detail.turnaroundTimeDays}d` : null,
              detail.expedited ? (
                <Badge key="exp" color="orange" variant="light" size="sm">
                  Expedited
                </Badge>
              ) : null,
            ]}
          />
        );
      case 'subjects':
        return (
          <Group gap={4} wrap="wrap">
            {(detail.subjectStrings ?? []).map((s, i) => (
              <Badge key={`${s}-${i}`} variant="light" color="blue" size="sm">
                {s}
              </Badge>
            ))}
          </Group>
        );
      case 'identification':
        return (
          <SummaryRow parts={[`${subjectCount} subject${subjectCount === 1 ? '' : 's'} matched`]} />
        );
      case 'partners':
        return <SummaryRow parts={['All items covered by locked partners']} />;
      case 'launch':
        return (
          <SummaryRow
            parts={[
              detail.triageStage === 'started' ? 'Started' : 'Ready to start',
              detail.reportLifecycleStage
                ? `report: ${detail.reportLifecycleStage.replace(/_/g, ' ')}`
                : null,
            ]}
          />
        );
    }
  };

  const stateFor = (idx: number): StageSectionState => {
    if (isRecord) {
      return 'record';
    }
    if (idx < currentIdx) {
      return 'completed';
    }
    if (idx === currentIdx) {
      return 'active';
    }
    return 'locked';
  };

  const bodyFor = (section: WorkspaceSection, state: StageSectionState): React.ReactNode => {
    // A section that isn't the active stage is read-only: the backend
    // rejects out-of-stage edits anyway, so the UI shouldn't offer them —
    // "Change" (revert) is the honest way back in.
    const readOnly = state !== 'active';
    switch (section.slug) {
      case 'define':
        return (
          <DefineSection
            detail={detail}
            updateInitial={triage.updateInitial}
            updateExtractionSources={triage.updateExtractionSources}
            registerSubmit={registerDefineSubmit}
            readOnly={readOnly}
            onSplit={handleSplit}
            onConfigCommercials={setConfigSeed}
          />
        );
      case 'subjects':
        return (
          <Stack gap="sm">
            {!readOnly && detail.reportConfigId === null && (
              <MissingConfigBanner detail={detail} onSave={triage.updateConfig} />
            )}
            <TriageSubjectsPanel
              reportRequestId={detail.id}
              readOnly={readOnly}
              onSplit={handleSplit}
              attention={subjectAttention}
              triageActions={{
                addSubject: triage.addSubject,
                editSubject: triage.editSubject,
                removeSubject: triage.removeSubject,
                reExtractSubject: triage.reExtractSubject,
              }}
            />
          </Stack>
        );
      case 'identification':
        return (
          <IdentificationSection
            reportRequestId={detail.id}
            reportId={detail.reportId}
            readOnly={readOnly}
            triageActions={{
              selectCandidate: triage.selectCandidate,
              clarifySubject: triage.clarifySubject,
              resolveSubjectManually: triage.resolveSubjectManually,
            }}
          />
        );
      case 'partners':
        return (
          <PartnersSection
            detail={detail}
            conflictDetail={partnersConflict}
            isActiveStage={detail.triageStage === 'np_selected'}
            readOnly={readOnly}
            onLockChanged={triage.refetch}
          />
        );
      case 'launch':
        return <LaunchSection detail={detail} teamName={teamName} />;
    }
  };

  // Revert options come from the effective order, strictly earlier only.
  const revertStages = detail.effectiveStages.filter(
    (s, idx) =>
      idx < detail.effectiveStages.indexOf(detail.triageStage) &&
      s !== 'rejected' &&
      s !== 'started'
  );

  return (
    <Stack gap="md">
      <WorkspaceHeader
        detail={detail}
        teamName={teamName}
        onRenamed={async (newFamilyKey) => {
          await triage.refetch();
          onFamilyRenamed?.(newFamilyKey);
        }}
      />

      {!embedded && (
        <FamilyStrip
          current={{ type: 'report_request', id: detail.id }}
          enquiryId={detail.enquiryId}
        />
      )}

      {/* Embedded in the case page, which carries the control itself.
          Shared at the enquiry when there is one, so every request under
          it follows; a rejected request is closed to sharing. */}
      {!embedded && detail.teamId !== null && detail.triageStage !== 'rejected' && (
        <CaseSharing
          anchor={
            detail.enquiryId
              ? { kind: 'enquiry', id: detail.enquiryId }
              : { kind: 'request', id: detail.id }
          }
        />
      )}

      <GateStrip
        sections={sections}
        currentSlug={isRecord ? null : currentSlug}
        allComplete={detail.triageStage === 'started'}
        blockers={gates.gates?.blockers ?? []}
        busyLabel={busyLabel}
        statusChip={
          detail.triageStage === 'started' ? (
            <Badge color="green" variant="filled" leftSection={<IconCircleCheck size={12} />}>
              Started
            </Badge>
          ) : detail.triageStage === 'rejected' ? (
            <Badge color="red" variant="filled" leftSection={<IconCircleX size={12} />}>
              Rejected
            </Badge>
          ) : undefined
        }
        primary={isRecord ? null : primary}
        onBlockerClick={onBlockerClick}
        onRevert={canRevert ? () => openRevertAt(null) : undefined}
        onReject={canReject ? openReject : undefined}
      />

      {detail.triageStage === 'started' && (
        <Alert color="green" icon={<IconCircleCheck size={18} />} data-testid="started-banner">
          This report has started
          {detail.reportLifecycleStage
            ? ` (${detail.reportLifecycleStage.replace(/_/g, ' ')})`
            : ''}
          .{' '}
          {detail.reportId && (
            <Text component="span" size="sm">
              Track progress at <Code>/reports/{detail.reportId}</Code>.
            </Text>
          )}
        </Alert>
      )}
      {detail.triageStage === 'rejected' && (
        <Alert color="red" icon={<IconCircleX size={18} />} data-testid="rejected-banner">
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              This request was rejected
              {detail.rejectedAt
                ? ` on ${new Date(detail.rejectedAt).toLocaleDateString('en-GB')}`
                : ''}
              .
            </Text>
            {detail.rejectionReason && <Text size="sm">Reason: {detail.rejectionReason}</Text>}
            <Text size="xs" c="dimmed">
              It will not be processed. The activity log records the rejection.
            </Text>
          </Stack>
        </Alert>
      )}

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {sections.map((section, idx) => {
              const state = stateFor(idx);
              return (
                <StageSection
                  key={section.slug}
                  slug={section.slug}
                  index={idx + 1}
                  label={section.label}
                  state={state}
                  expanded={expandedSlug === section.slug && state !== 'locked'}
                  onToggle={(open) => setExpanded(open ? section.slug : null)}
                  summary={
                    state === 'completed' || state === 'record'
                      ? summaryFor(section.slug)
                      : undefined
                  }
                  lockedHint={
                    idx > 0 ? `Complete ${sections[idx - 1].label} first.` : 'Cannot start yet.'
                  }
                  onChange={
                    state === 'completed' ? () => openRevertAt(section.stages[0]) : undefined
                  }
                  flashKey={flashKeys[section.slug] ?? 0}
                >
                  {bodyFor(section, state)}
                </StageSection>
              );
            })}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <WorkspaceRail
            detail={detail}
            teamName={teamName}
            updatePricing={triage.updatePricing}
            configSeed={configSeed}
          />
        </Grid.Col>
      </Grid>

      <RevertModal
        opened={revertOpen}
        onClose={closeRevert}
        target={revertTarget}
        setTarget={setRevertTarget}
        options={revertStages}
        onConfirm={async (target, reason) => {
          await triage.revert(target, reason);
          closeRevert();
          setRevertTarget(null);
        }}
      />

      <RejectModal
        opened={rejectOpen}
        onClose={closeReject}
        onConfirm={async (reason) => {
          await triage.reject(reason);
          closeReject();
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Identity header
// ---------------------------------------------------------------------------

function WorkspaceHeader({
  detail,
  teamName,
  onRenamed,
}: Readonly<{
  detail: ReportRequestDetail;
  teamName: string | null;
  onRenamed: (newFamilyKey: string) => void | Promise<void>;
}>) {
  // Codename renders as {family}{suffix}; the rename applies to the
  // family portion, so split the rendered form on the family key.
  const family = detail.codenameFamily;
  const suffix =
    family && detail.codename?.startsWith(family) ? detail.codename.slice(family.length) : null;

  return (
    <Group justify="space-between" align="flex-start" wrap="wrap">
      <Stack gap={2}>
        {family ? (
          <EditableCodename
            value={family}
            suffix={suffix}
            size="md"
            fw={700}
            onSaved={(newFamilyKey) => onRenamed(newFamilyKey)}
          />
        ) : (
          <Title order={3}>Request {detail.id.slice(0, 8)}…</Title>
        )}
        <Group gap={6}>
          <Text size="sm" c="dimmed">
            {teamName ?? detail.teamId ?? 'No team'}
          </Text>
          {detail.configName && (
            <>
              <Text size="sm" c="dimmed">
                ·
              </Text>
              {detail.reportConfigId ? (
                <Anchor
                  component={Link}
                  to={`/admin/report-configs/${detail.reportConfigId}`}
                  size="sm"
                  c="dimmed"
                  underline="hover"
                  data-testid="header-config-link"
                >
                  {detail.configName}
                </Anchor>
              ) : (
                <Text size="sm" c="dimmed">
                  {detail.configName}
                </Text>
              )}
            </>
          )}
          {detail.price && (
            <>
              <Text size="sm" c="dimmed">
                ·
              </Text>
              <Text size="sm" c="dimmed">
                {formatMoney(detail.price, detail.currency)}
              </Text>
            </>
          )}
          {detail.turnaroundTimeDays != null && (
            <>
              <Text size="sm" c="dimmed">
                ·
              </Text>
              <Text size="sm" c="dimmed">
                {detail.turnaroundTimeDays}d TAT
              </Text>
            </>
          )}
          {detail.expedited && (
            <Badge color="orange" variant="light" size="sm">
              ⚡ Expedited
            </Badge>
          )}
        </Group>
      </Stack>
      <Badge size="lg" variant="light" color={detail.triageStage === 'rejected' ? 'red' : 'blue'}>
        {triageStageLabel(detail.triageStage)}
      </Badge>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Revert + reject modals (ported from the retired side panel)
// ---------------------------------------------------------------------------

function RevertModal({
  opened,
  onClose,
  target,
  setTarget,
  options,
  onConfirm,
}: Readonly<{
  opened: boolean;
  onClose: () => void;
  target: TriageStage | null;
  setTarget: (t: TriageStage | null) => void;
  options: TriageStage[];
  onConfirm: (target: TriageStage, reason: string) => Promise<void>;
}>) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!target) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(target, reason);
      setReason('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Revert triage stage">
      <Stack gap="sm">
        <Select
          label="Revert to"
          data={options.map((s) => ({ value: s, label: triageStageLabel(s) }))}
          value={target}
          onChange={(value) => setTarget(value as TriageStage | null)}
        />
        <Textarea
          label="Reason"
          placeholder="Optional"
          minRows={3}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          Reverting clears artefacts produced after the target stage:
          <br />• Reverting past <Code>plan_built</Code> clears the research plan and NP
          assignments.
          <br />• Reverting past <Code>subjects_identified</Code> clears identification candidates.
          <br />• Reverting to <Code>initial</Code> also clears extracted subjects.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => void handleConfirm()}
            loading={submitting}
            disabled={!target}
            data-testid="revert-confirm"
          >
            Confirm revert
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RejectModal({
  opened,
  onClose,
  onConfirm,
}: Readonly<{
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}>) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
      setReason('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Reject this request">
      <Stack gap="sm">
        <Text size="sm">
          This is terminal — the report (if any) is also dropped to FAILED. Open inbox items for
          this request will auto-resolve.
        </Text>
        <Textarea
          label="Reason (optional)"
          placeholder="e.g. Out of scope; client withdrew"
          minRows={3}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color="red" onClick={() => void handleConfirm()} loading={submitting}>
            Confirm reject
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Shown at the Subjects step when the request still has no report
 * config — extraction takes a stab at deriving one, but when it can't
 * (or the flow never ran extraction) the onward stage order can't be
 * computed, so the gate strip blocks until it's settled here. */
function MissingConfigBanner({
  detail,
  onSave,
}: Readonly<{
  detail: ReportRequestDetail;
  onSave: (configId: string) => Promise<unknown>;
}>) {
  const { configs } = useReportConfigs({ teamId: detail.teamId ?? undefined, isActive: true });
  const [picked, setPicked] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!picked) {
      return;
    }
    setSaving(true);
    try {
      await onSave(picked);
    } catch {
      // The hook toasts the failure; keep the banner for another go.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Alert color="yellow" title="Pick a report config" data-testid="missing-config-banner">
      <Stack gap="xs">
        <Text size="sm">
          Extraction couldn&apos;t determine the report type for this request. Pick one to continue
          — it drives the remaining steps, scope and pricing defaults.
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Select
            size="xs"
            style={{ flex: 1, maxWidth: 360 }}
            placeholder="Select a config"
            data={configs.map((c) => ({
              value: c.id,
              label: c.legacyFlow ? `${c.name} · legacy` : c.name,
            }))}
            value={picked}
            onChange={setPicked}
            searchable
          />
          <Button
            size="xs"
            disabled={!picked}
            loading={saving}
            onClick={() => void handleSave()}
            data-testid="missing-config-save"
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
